import { ApolloQueryResult } from '@apollo/client';
import {
  ExecuteCodeAgainstTestCasesQuery,
  ExecutionStatus,
  GetAssessmentQuery,
  GetLatestCodeExecutionQuery,
} from '@gql/gql-types';
import { useCallback, useMemo, useRef } from 'react';

import { processExecutionResult } from './live-coding.utils';
import { TestCase, TestCaseFromTemplate } from './types';

type UseCodeExecutionHandlerProps = {
  assessment: NonNullable<GetAssessmentQuery['assessment']>;
  setConsoleOutput: (output: string) => void;
  setTestResults: (results: TestCase[]) => void;
  isSubmitting: boolean;
  setShowBadStateSubmissionConfirmModal: (show: boolean) => void;
  setHasUnviewedConsoleOutput: (hasUnviewed: boolean) => void;
  currentTestTab: number;
  isServerTriggeredCompletion: boolean;
  handleFinalSubmit: () => void;
  refetchLatestExecution: () => Promise<
    ApolloQueryResult<GetLatestCodeExecutionQuery>
  >;
};

export const useCodeExecutionHandler = ({
  assessment,
  setConsoleOutput,
  setTestResults,
  isSubmitting,
  setShowBadStateSubmissionConfirmModal,
  setHasUnviewedConsoleOutput,
  currentTestTab,
  isServerTriggeredCompletion,
  handleFinalSubmit,
  refetchLatestExecution,
}: UseCodeExecutionHandlerProps) => {
  const prevConsoleOutputRef = useRef<string>('');
  const prevExecutionResultRef = useRef<
    ExecuteCodeAgainstTestCasesQuery['executeCodeAgainstTestCases'] | null
  >(null);

  const handleExecutionCompleted = useCallback(
    async (data: ExecuteCodeAgainstTestCasesQuery) => {
      const executionResult = data.executeCodeAgainstTestCases;

      const showPillIfOutputChanged = (newOutput: string) => {
        const isNotOnConsoleTab = currentTestTab !== 1;
        const hasOutputChanged = newOutput !== prevConsoleOutputRef.current;
        const hasExecutionResultChanged =
          JSON.stringify(executionResult) !==
          JSON.stringify(prevExecutionResultRef.current);

        if (
          (isNotOnConsoleTab && hasOutputChanged) ||
          hasExecutionResultChanged
        ) {
          setHasUnviewedConsoleOutput(true);
        }
      };

      if (executionResult.status === ExecutionStatus.Timeout) {
        const newOutput = `Execution error:\n${executionResult.stderr || 'Code execution timed out.'}`;
        setConsoleOutput(newOutput);
        const newTestResults = (
          assessment.template.filteredAttributes?.testCases || []
        ).map((tc: TestCaseFromTemplate) => ({
          id: tc.id,
          input: tc.input,
          expected: tc.expectedOutput,
          status: 'timeout',
          error: 'Execution timed out',
        }));
        setTestResults(newTestResults);

        showPillIfOutputChanged(newOutput);
        prevConsoleOutputRef.current = newOutput;
        prevExecutionResultRef.current = executionResult;

        if (isSubmitting && !isServerTriggeredCompletion) {
          setShowBadStateSubmissionConfirmModal(true);
        }
        return;
      }

      if (executionResult.status === ExecutionStatus.Error) {
        const newOutput = `Compilation error:\n${executionResult.stderr || executionResult.stdout || ''}`;
        setConsoleOutput(newOutput);
        const newTestResults = (
          assessment.template.filteredAttributes?.testCases || []
        ).map((tc: TestCaseFromTemplate) => ({
          id: tc.id,
          input: tc.input,
          expected: tc.expectedOutput,
          status: 'failed',
          error: 'Compilation failed',
        }));
        setTestResults(newTestResults);

        showPillIfOutputChanged(newOutput);
        prevConsoleOutputRef.current = newOutput;
        prevExecutionResultRef.current = executionResult;

        if (isSubmitting && !isServerTriggeredCompletion) {
          setShowBadStateSubmissionConfirmModal(true);
        }
        return;
      }

      if (executionResult.status !== ExecutionStatus.Success) {
        const newOutput = `Execution error:\n${executionResult.stderr || executionResult.stdout || ''}`;
        setConsoleOutput(newOutput);

        showPillIfOutputChanged(newOutput);
        prevConsoleOutputRef.current = newOutput;
        prevExecutionResultRef.current = executionResult;

        if (isSubmitting && !isServerTriggeredCompletion) {
          setShowBadStateSubmissionConfirmModal(true);
        }
        return;
      }

      const { testCases, consoleOutput } = processExecutionResult(
        executionResult,
        assessment.template.filteredAttributes?.testCases || [],
      );
      setTestResults(testCases);
      setConsoleOutput(consoleOutput);

      showPillIfOutputChanged(consoleOutput);
      prevConsoleOutputRef.current = consoleOutput;
      prevExecutionResultRef.current = executionResult;

      await refetchLatestExecution();

      if (isSubmitting) {
        const allPassed = testCases.every((tc) => tc.status === 'passed');
        if (!allPassed && !isServerTriggeredCompletion) {
          setShowBadStateSubmissionConfirmModal(true);
        } else {
          handleFinalSubmit();
        }
      }
    },
    [
      assessment,
      setConsoleOutput,
      setTestResults,
      isSubmitting,
      setShowBadStateSubmissionConfirmModal,
      setHasUnviewedConsoleOutput,
      currentTestTab,
      isServerTriggeredCompletion,
      handleFinalSubmit,
      refetchLatestExecution,
    ],
  );

  return useMemo(
    () => ({
      handleExecutionCompleted,
    }),
    [handleExecutionCompleted],
  );
};
